/**
 * WeekShapeSheet — Growth Engine 2b (Feb 17, 2026).
 *
 * Detail panel for the read-only operational week X-ray.
 * Native-feeling per device:
 *   • Mobile (<md):  uses canonical <MobileBottomSheet bareContent>
 *   • Desktop (≥md): renders as a right-rail slide-over
 *
 * Day rows are FIXED tag tokens (`free` / `quiet` / `calm` /
 * `tight cluster` / `scatter`) — operator-locked vocabulary. No
 * percentages, no scores, no rankings. Tap a day → expands to the
 * lesson list for that day. All wording originates server-side
 * from `OBSERVATION_REGISTRY`; this component renders the tag and
 * the data verbatim.
 */
import { useState } from "react";
import { MobileBottomSheet } from "../mobile";
import { X } from "lucide-react";

const TAG_STYLE = {
  free:            { label: "free",            cls: "bg-slate-50  text-slate-600  dark:bg-slate-800/50  dark:text-slate-300" },
  quiet:           { label: "quiet",           cls: "bg-amber-50  text-amber-700  dark:bg-amber-900/20  dark:text-amber-200" },
  calm:            { label: "calm",            cls: "bg-emerald-50 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-200" },
  "tight cluster": { label: "tight cluster",   cls: "bg-indigo-50 text-indigo-700 dark:bg-indigo-900/20 dark:text-indigo-200" },
  scatter:         { label: "scatter",         cls: "bg-rose-50   text-rose-700   dark:bg-rose-900/20    dark:text-rose-200" },
};

function DayRow({ day }) {
  const [expanded, setExpanded] = useState(false);
  const style = TAG_STYLE[day.tag] || TAG_STYLE.calm;
  const summary = day.lesson_count === 0
    ? "0 lessons"
    : `${day.lesson_count} ${day.lesson_count === 1 ? "lesson" : "lessons"} · ${day.area_count} ${day.area_count === 1 ? "area" : "areas"}`;

  return (
    <li
      data-testid={`week-shape-day-row-${day.date}`}
      className="border border-slate-200 dark:border-slate-700 rounded-xl bg-white dark:bg-slate-900"
    >
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center gap-3 px-3.5 py-3 text-left"
      >
        <div className="flex-1 min-w-0">
          <p className="text-sm font-bold text-slate-900 dark:text-white">
            {day.weekday_long}
            <span className="ml-1.5 text-[11px] font-normal text-slate-500 dark:text-slate-400">
              {day.date}
            </span>
          </p>
          <p className="text-[12px] text-slate-500 dark:text-slate-400 mt-0.5">
            {summary}
          </p>
        </div>
        <span
          data-testid={`week-shape-day-tag-${day.tag.replace(/\s+/g, "-")}`}
          className={`text-[10px] font-bold uppercase tracking-wide px-2.5 py-1 rounded-full ${style.cls}`}
        >
          {style.label}
        </span>
      </button>
      {expanded && (day.areas?.length || day.largest_window) ? (
        <div className="px-3.5 pb-3 text-[12px] text-slate-600 dark:text-slate-300 space-y-1">
          {day.areas?.length ? (
            <p data-testid={`week-shape-day-areas-${day.date}`}>
              Areas: {day.areas.join(", ")}
            </p>
          ) : null}
          {day.largest_window ? (
            <p data-testid={`week-shape-day-window-${day.date}`}>
              Largest gap: {day.largest_window[0]}h ({day.largest_window[1]} – {day.largest_window[2]})
            </p>
          ) : null}
        </div>
      ) : null}
    </li>
  );
}

function SheetBody({ data }) {
  const days = data?.days || [];
  return (
    <div className="space-y-2.5" data-testid="week-shape-sheet-body">
      <p className="text-[11px] text-slate-500 dark:text-slate-400 mb-1">
        {data?.week_start} – {data?.week_end}
      </p>
      <ul className="space-y-2">
        {days.map((d) => <DayRow key={d.date} day={d} />)}
      </ul>
    </div>
  );
}

export default function WeekShapeSheet({ open, onClose, data }) {
  if (!open) return null;

  return (
    <>
      {/* Mobile: canonical bottom-sheet primitive (handles backdrop, ESC, scroll-lock, safe-area). */}
      <div className="md:hidden">
        <MobileBottomSheet
          open={open}
          onClose={onClose}
          testid="week-shape-sheet"
          title="This week's shape"
          description="A calm read of how your week looks across days and postcode areas."
        >
          <SheetBody data={data} />
        </MobileBottomSheet>
      </div>

      {/* Desktop: right-rail slide-over. */}
      <div
        className="hidden md:block fixed inset-0 z-[1100]"
        data-testid="week-shape-sheet-desktop"
        role="dialog"
        aria-modal="true"
      >
        <div
          className="absolute inset-0 bg-slate-900/30 backdrop-blur-sm"
          onClick={onClose}
        />
        <aside className="absolute right-0 top-0 bottom-0 w-full max-w-md bg-white dark:bg-slate-900 border-l border-slate-200 dark:border-slate-800 shadow-xl overflow-y-auto">
          <header className="sticky top-0 z-10 flex items-center justify-between gap-3 px-5 py-4 border-b border-slate-200 dark:border-slate-800 bg-white/95 dark:bg-slate-900/95 backdrop-blur">
            <div>
              <p className="text-base font-bold text-slate-900 dark:text-white">This week's shape</p>
              <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5">
                A calm read of how your week looks across days and postcode areas.
              </p>
            </div>
            <button
              type="button"
              data-testid="week-shape-sheet-close"
              onClick={onClose}
              className="w-8 h-8 rounded-full bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 inline-flex items-center justify-center transition-colors"
              aria-label="Close"
            >
              <X className="w-4 h-4 text-slate-700 dark:text-slate-200" />
            </button>
          </header>
          <div className="p-5">
            <SheetBody data={data} />
          </div>
        </aside>
      </div>
    </>
  );
}
